import {
  SessionExpiredError,
  UnauthenticatedError,
} from '@/auth/sessionExpiry';
import type {
  StudyDetailResponse,
  StudyStatus,
} from '@/api/generated/types.gen';

/**
 * Classifies a failed detail-query error on a `/share/...` public viewer
 * route as "the visitor needs to sign in to see this" vs. any other failure
 * (network error, 500, etc. — those get the generic error state instead).
 *
 * Two shapes cover the backend's access-denied response for a private/
 * restricted resource when the caller isn't authenticated (or is
 * authenticated but not permitted):
 *  - A 401, as one of the transport's two typed auth failures:
 *    `UnauthenticatedError` (an anonymous visitor sent no credentials — the
 *    common case here) or `SessionExpiredError` (a real session died
 *    mid-visit). Matching on the types rather than on `.status` matters: a 401
 *    with an empty or non-object body reaches consumers as `{}`, which carries
 *    no status at all, and this route would then offer "couldn't load" instead
 *    of the sign-in CTA it exists to show.
 *  - A plain 404 (the backend's deliberate "don't reveal whether this
 *    exists" response for access-denied) surfaces as a thrown error object
 *    tagged with `.status` by `attachAuthInterceptors.ts`'s error interceptor.
 */
export function isAccessDeniedError(error: unknown): boolean {
  if (error instanceof SessionExpiredError) return true;
  if (error instanceof UnauthenticatedError) return true;
  return httpStatus(error) === 404;
}

/**
 * The HTTP status a failed generated-client call carries, if any.
 *
 * hey-api throws the parsed error body, not an `Error`, and
 * `attachStatusInterceptor` tags it with `.status` — so a 404 is found here,
 * never in an error message.
 */
export function httpStatus(error: unknown): number | undefined {
  return error && typeof error === 'object' && 'status' in error
    ? (error as { status?: number }).status
    : undefined;
}

/**
 * Effective-role checks for a SimOps / EvalTool study.
 *
 * `my_role` is stamped on `StudyDetailResponse` by the BE per-request
 * (see ENG-1323: no per-tab scoped filtering; a viewer sees the full
 * response, FE gates every mutating affordance). These helpers are the
 * single choke point every eval / analyze surface reads before
 * rendering a button that would fire a mutation.
 *
 * Wire tiers (from `ShareRole` + `my_role` on the response):
 *   `owner`     — full mutation + sharing + visibility + delete
 *   `editor`    — full mutation, no reshare / delete
 *   `commenter` — read + write comments only, no config mutation
 *   `viewer`    — read only
 *
 * `my_role` is optional on the wire — a pre-ENG-1323 payload or a study
 * opened via an unauthenticated share link may omit it. Treat missing
 * as **least privilege** everywhere (viewer for gates, below-editor for
 * redirect) so a stale response never surfaces a mutation the user
 * can't run AND never lands them on an editable chrome that would 403
 * every action.
 */

/** Owner + editor. Everything else is read-only for study config /
 *  DP submission / decide / AI retry etc. */
export function canEditStudy(
  study: StudyDetailResponse | null | undefined,
): boolean {
  const role = study?.my_role;
  return role === 'owner' || role === 'editor';
}

/** Owner + editor + commenter. Use for comment threads and future
 *  Conclusion / Discussion textbox that persists user prose. */
export function canCommentOnStudy(
  study: StudyDetailResponse | null | undefined,
): boolean {
  const role = study?.my_role;
  return role === 'owner' || role === 'editor' || role === 'commenter';
}

/** Owner only — sharing, visibility toggle, delete. Editors can mutate
 *  the study config + submit DPs but can't reshare or delete (matches
 *  BE `service.py` gates). */
export function canOwnStudy(
  study: StudyDetailResponse | null | undefined,
): boolean {
  return study?.my_role === 'owner';
}

/** Effective role for display copy. Missing / unknown → `null` (callers
 *  should treat as "no access" rather than falsely showing "viewer" —
 *  the user might not even be logged in). */
export function effectiveStudyRole(
  study: StudyDetailResponse | null | undefined,
): 'owner' | 'editor' | 'commenter' | 'viewer' | null {
  const role = study?.my_role;
  if (
    role === 'owner' ||
    role === 'editor' ||
    role === 'commenter' ||
    role === 'viewer'
  ) {
    return role;
  }
  return null;
}

/**
 * Which read-only banner a `/share/...` visitor should see, or `null` for none.
 *
 * A signed-out visitor is the primary audience for a public link, and `my_role`
 * says nothing about them — they hold no role at all — so they get the generic
 * "view-only" banner rather than no banner, which left the page silently
 * disabled with no explanation. Signed-in viewers/commenters get their role
 * named. Owners/editors get `null` here: the share routes offer them the
 * "open in app" banner instead of a read-only one.
 */
export function readOnlyBannerRole(
  isAuthenticated: boolean,
  myRole: string | null | undefined,
): 'viewer' | 'commenter' | 'anonymous' | null {
  if (!isAuthenticated) return 'anonymous';
  if (isBelowEditorRole(myRole)) return myRole;
  return null;
}

/**
 * Whether a `/share/...` visitor should be offered the "open in app" banner:
 * a signed-in owner or editor, the counterpart of `readOnlyBannerRole`'s
 * `null`. An allow-list, not `!isBelowEditorRole(...)` — that one lets an
 * unrecognised role through on purpose (see its docstring), and here an
 * unknown role must not be sent into an editable workspace.
 */
export function canOpenInApp(
  isAuthenticated: boolean,
  myRole: string | null | undefined,
): boolean {
  return isAuthenticated && (myRole === 'owner' || myRole === 'editor');
}

/**
 * Whether a resource role is below editor, i.e. the holder can look but not
 * change anything. The three product routes (`/meshgen/$uuid`,
 * `/diagnosis/$uuid`, `/simops/$studyId`) use this to send such a visitor to
 * the read-only `/share/...` viewer instead of an editable workspace whose
 * controls would all fail server-side.
 *
 * Deliberately an allow-list of the two known read-only roles rather than
 * "not editor/owner": `my_role` is typed `string | null` off the generated
 * client, so an unrecognised or not-yet-loaded value must fall through to
 * "leave them alone" — treating unknown as below-editor would redirect an
 * owner out of their own workspace on first paint (before `my_role` lands).
 *
 * Note the asymmetry with `canEditStudy`: this returns `false` for null
 * (leave alone), while `canEditStudy(null)` returns `false` (least
 * privilege — no mutating button until we know they can). Different
 * axes: one drives redirects (bounce = louder failure than leaving
 * them on a page whose actions 403 later), the other drives buttons.
 */
export function isBelowEditorRole(
  myRole: string | null | undefined,
): myRole is 'viewer' | 'commenter' {
  return myRole === 'viewer' || myRole === 'commenter';
}

/**
 * Study phases where the editable chrome carries a study-wide mutation
 * surface that is NOT per-button gated by `canEditStudy` — today only
 * `draft` (input picking + sweep authoring). Read-only tiers are bounced away
 * from it by `_app/simops/$studyId.tsx`; the `/share/study/` auto-forward
 * mirrors the list so a signed-in viewer/commenter doesn't ping-pong between
 * the two routes.
 *
 * `awaiting_user_meshing` left the set once its two dialogs
 * (`CompletionConfirmDialog` + `CancelStudyDialog`) were gated: a read-only
 * reader now gets the meshes view-only (`isStudyMeshingReadOnly`). Shrink
 * this set the same way as per-button gates land.
 */
export const READ_ONLY_BLOCKED_PHASES: ReadonlySet<StudyStatus> =
  new Set<StudyStatus>(['draft']);

/** Whether a read-only visitor can safely be auto-forwarded from
 *  `/share/study/` into the in-app workbench for this study status. */
export function canAutoForwardReadOnly(status: StudyStatus): boolean {
  return !READ_ONLY_BLOCKED_PHASES.has(status);
}

/**
 * True when the visitor is a signed-in known role (owner / editor /
 * commenter / viewer) — used by `/share/study/` to decide whether to
 * auto-forward into the in-app workbench. Falls back to false for
 * missing / unknown / anonymous.
 */
export function isKnownSignedInRole(
  isAuthenticated: boolean,
  study: StudyDetailResponse | null | undefined,
): boolean {
  return isAuthenticated && effectiveStudyRole(study) != null;
}

/** Study mesh workspaces use the study's grant, not the geometry's grant. */
export function canEditMeshWorkspace({
  geometryRole,
  studyRole,
  inStudy,
}: {
  geometryRole: string | null | undefined;
  studyRole: string | null | undefined;
  inStudy: boolean;
}): boolean {
  const role = inStudy ? studyRole : geometryRole;
  return role === 'owner' || role === 'editor';
}
